import { Component, Input, OnInit } from '@angular/core';
import { PolicyService } from '../../services/policy.service';
import Policy from '../../../../core/data/models/policy.mode';
import { TableComponent } from '../../../../common/components/table/table.component';
import { TableContainerComponent } from '../../../../common/components/table-container/table-container.component';
import TableHeader from '../../../../common/interfaces/table/tableHeader';
import BaseListComponent from '../../../../common/components/base-list/base-list.component';
import { SwalService } from '../../../../common/services/swal.service';
import { TableColumnDirective } from '../../../../common/directives/table-column.directive';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../auth/services/auth.service';
import { SettingService } from '../../services/setting.service';
import { Setting } from '../../../../core/data/models/setting.model';
import { lastValueFrom } from 'rxjs';
import PolicyTypes from '../../../../common/enums/policyTypes';
import { SharedModule } from '../../../../common/shared.module';
import { policyMask } from '../../../../common/constants/masks';
import { ClientSelectorComponent } from '../../../../common/components/client-selector/client-selector.component';
import ProducerClientDto from '../../../../core/data/dtos/producer-client.dto';
import { ClientButtonComponent } from '../../../../common/components/client-button/client-button.component';
import TableFilter from '../../../../common/interfaces/table/tableFilter';

@Component({
  selector: 'app-policy-list',
  providers: [PolicyService, SettingService],
  imports: [
    TableComponent,
    TableContainerComponent,
    TableColumnDirective,
    SharedModule,
    ClientSelectorComponent,
    ClientButtonComponent,
  ],
  templateUrl: './policy-list.component.html',
  styleUrl: './policy-list.component.css',
})
export class PolicyListComponent
  extends BaseListComponent<Policy>
  implements OnInit
{
  policies: Policy[] = [];
  filteredPolicies: typeof this.policies = [];
  setting: Setting = {} as any;
  policyMask = policyMask;
  client?: ProducerClientDto;

  policyFilter?: PolicyTypes;

  activeFilter: string = '';

  get typeFilters(): TableFilter[] {
    const filters: TableFilter[] = [{ label: 'Todos', value: '' }];
    if (this.setting?.[PolicyTypes.Vehicle]?.length) {
      filters.push({ label: 'Vehículos', value: PolicyTypes.Vehicle });
    }
    if (this.setting?.[PolicyTypes.House]?.length) {
      filters.push({ label: 'Propiedades', value: PolicyTypes.House });
    }
    if (this.setting?.[PolicyTypes.Travel]?.length) {
      filters.push({ label: 'Viajes', value: PolicyTypes.Travel });
    }
    return filters;
  }
  private searchValue: string = '';

  constructor(
    private policyService: PolicyService,
    private authService: AuthService,
    private router: Router,
    private settingService: SettingService,
    private swalService: SwalService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  get isIntermediary() {
    return this.authService.isIntermediary;
  }

  get isClient() {
    return this.authService.isClient;
  }

  get tableHeaders() {
    const result: TableHeader[] = [
      {
        label: 'Tipo de Póliza',
        attribute: 'product',
      },
      {
        label: 'Nombre',
        attribute: 'debtorName',
      },
      {
        label: 'Póliza',
        attribute: 'policyNumber',
      },
      {
        label: 'Fecha expiración',
        attribute: 'expirationDate',
        type: 'date',
      },
    ];

    return result;
  }

  override ngOnInit(): void {
    // Initialize filter from query params if provided
    const typeParam = this.route.snapshot.queryParamMap.get(
      'type',
    ) as PolicyTypes | null;
    const validTypes = Object.values(PolicyTypes);
    if (typeParam && validTypes.includes(typeParam)) {
      this.activeFilter = typeParam;
      this.policyFilter = typeParam;
    }

    if (!this.isIntermediary) {
      this.getPolicies();
    }
  }

  getPolicies = async () => {
    if (this.isIntermediary && !this.client) return;

    this.swalService.showLoading();
    const result = await lastValueFrom(this.settingService.getPolicyTypes());
    this.setting = result.data;
    if (this.policyFilter && !this.setting?.[this.policyFilter]?.length) {
      this.policyFilter = undefined;
      this.activeFilter = '';
    }
    const user = this.authService.user;

    try {
      const result = await lastValueFrom(
        this.policyService.getMyPolicies(
          this.isIntermediary
            ? this.client!.idnumber!
            : user.idTokenClaims?.extension_IdNumber!,
        ),
      );

      if (this.policyFilter) {
        result.data = result.data.filter((policy) =>
          this.setting[this.policyFilter!]?.includes(policy.slob.code),
        );
      }

      this.policies = result.data;
      // Apply any active filters after loading data
      this.applyFilters();
      this.swalService.dismiss();
    } catch (e) {
      this.swalService.error('No se pudo obtener las pólizas');
    }
  };

  showDetails(policy: Policy) {
    const code = policy.slob.code;
    const types = Object.keys(PolicyTypes);
    for (let i in types) {
      const typeKey = types[i] as keyof typeof PolicyTypes;
      const type = PolicyTypes[typeKey];
      if (this.setting[type]?.includes(code)) {
        this.router.navigate([
          `/policy/${type}/${policy.policyNumber}/${policy.slob.code}/detail`,
        ]);
        return;
      }
    }
    this.swalService.error(
      'No se pudo acceder al detalle de la póliza',
      'warning',
    );
  }

  handleSelectedClient($event: ProducerClientDto) {
    this.client = $event;
    this.getPolicies();
  }

  filterPolicies(filter: string) {
    this.searchValue = filter || '';
    this.applyFilters();
  }

  handleTypeFilter(value: string) {
    this.activeFilter = value;
    // Map activeFilter to component's policyFilter for reuse
    this.policyFilter = (value as PolicyTypes) || undefined;
    this.applyFilters();
  }

  private applyFilters() {
    // Start from full list
    let base = [...this.policies];

    // Apply type filter if any
    if (this.policyFilter) {
      base = base.filter((policy) =>
        this.setting[this.policyFilter!]?.includes(policy.slob.code),
      );
    }

    // Apply search filter if any
    const filter = this.searchValue?.trim();
    if (filter) {
      const normalize = (text: string) =>
        text
          ?.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      const normalizedFilter = normalize(filter);
      base = base.filter(
        (policy) =>
          normalize(policy.debtorName)?.includes(normalizedFilter) ||
          normalize(policy.product?.description)?.includes(normalizedFilter) ||
          normalize(policy.policyNumber)?.includes(normalizedFilter),
      );
    }

    this.filteredPolicies = base;
  }
}
